import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonIcon, Icon, Typography } from '@saviynt/design-system';

import type { RootState } from '../../../../services/serviceTypes';
import { type CopilotState } from '../../data-layer/types';
import {
  addVisitedStep,
  setActiveStep,
  setFormStatus,
  setSavedStepData,
} from '../../data-store/copilotSlice';
import useAddAccessAttributeMappingLogic from '../../hooks/addAccessAttributeMappingLogic';
import { useAddAccessLogic } from '../../hooks/addAccessLogic';
import useRemoveAccessLogic from '../../hooks/removeAccessLogic';
import { useChatFlowController } from '../../hooks/useChatFlowController';
import useFormValidation from '../../hooks/useFormValidation';
import { useFullscreenRedux } from '../../hooks/useFullscreenRedux';
import StepValidationService from '../../services/stepValidationService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import StepRenderer from '../StepRenderer/StepRenderer';

import AddAccessExpandedView from './AddAccessExpandedView';
import AttributeMappingExpandedView from './AttributeMappingExpandedView';
import RemoveAccessAttributeMappingExpandedView from './RemoveAccessAttributeMappingExpandedView';

import './ExpandedFormView.css';

/**
 * Expanded form vie for all the steps
 */
function ExpandedFormView() {
  const {
    isFullscreen,
    fullScreenStep,
    fullScreenButtonKind,
    fullScreenButtonLabel,
    fullScreenEntitlementIndex,
    setIsFullScreen,
  } = useFullscreenRedux();

  const dispatch = useDispatch();
  const [, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const addAccessInputField = useSelector(
    (state: { copilot: CopilotState }) => state.copilot.ui.addAccessInputField
  );

  // Remove savingSteps dependency - buttons will be managed by form state
  // const isAddAccessSaving = false;
  // const isRemoveAccessSaving = false;

  const getActualStepName = (displayName: string): string => {
    if (displayName.includes('Mapping')) {
      return 'AddAccessAttributeMapping';
    }

    if (displayName.includes('Remove Access for')) {
      return 'RemoveAccess';
    }

    const stepMapping: Record<string, string> = {
      'Application Details': 'General',
      'Connection Setup': 'Connection',
      Provisioning: 'Provisioning',
      'Create Account': 'CreateAccount',
      'Update Account': 'UpdateAccount',
      'Enable Account': 'EnableAccount',
      'Disable Account': 'DisableAccount',
      'Remove Account': 'RemoveAccount',
      'Add Access': 'AddAccess',
      'Remove Access': 'RemoveAccess',
      Confirmation: 'Confirmation',
    };

    return stepMapping[displayName] || displayName;
  };

  const actualStepName = getActualStepName(fullScreenStep);

  const formData = useSelector(
    (state: RootState) => state.copilot.stepFormData[actualStepName]
  );

  const currentStepFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData
  );

  const { validateForm, localFields, resetValidationState } =
    useFormValidation();
  const { handleFormSubmit } = useChatFlowController();

  const addAccessLogic = useAddAccessLogic(handleFormSubmit);
  const addAccessAttributeMappingLogic = useAddAccessAttributeMappingLogic();
  const removeAccessLogic = useRemoveAccessLogic();

  const handleSave = useCallback(async () => {
    setIsLoading(true);
    setValidationError(null);
    resetValidationState();

    try {
      if (actualStepName === 'AddAccess') {
        const saveResult = addAccessLogic.handleSave();

        if (saveResult) {
          setIsFullScreen(false);
        }

        return;
      }

      if (actualStepName === 'AddAccessAttributeMapping') {
        // Use same logic as regular form
        const template = String(
          currentStepFormData.attributeDefaultValue ?? ''
        );
        const result = addAccessAttributeMappingLogic.handleNext(
          fullScreenEntitlementIndex,
          template,
          handleFormSubmit
        );

        if (result) {
          setIsFullScreen(false);
        }

        return;
      }

      if (actualStepName === 'RemoveAccess') {
        // Use same logic as regular form
        const template = String(
          currentStepFormData.attributeDefaultValue ?? ''
        );
        const result = removeAccessLogic.handleNext(
          fullScreenEntitlementIndex,
          template,
          handleFormSubmit
        );

        if (result) {
          setIsFullScreen(false);
        }

        return;
      }

      // Regular step validation and submission for other steps
      const dataToValidate = formData;

      const validation = await StepValidationService.validateStep(
        actualStepName,
        dataToValidate,
        validateForm
      );

      if (validation.isValid) {
        // Save to savedStepData on successful validation
        dispatch(
          setSavedStepData({
            stepName: actualStepName,
            formData: dataToValidate,
          })
        );
        dispatch(setActiveStep(actualStepName));
        dispatch(
          setFormStatus({
            stepName: actualStepName,
            formStatus: 'collapsedview',
          })
        );

        setIsFullScreen(false);

        setTimeout(() => {
          handleFormSubmit(actualStepName, dataToValidate, validation.isValid);
        }, 50);
      } else {
        setValidationError(validation.errors[0] || 'Validation failed');
      }
    } catch (err) {
      // Handle unexpected errors
    } finally {
      setIsLoading(false);
    }
  }, [
    resetValidationState,
    actualStepName,
    formData,
    validateForm,
    addAccessLogic,
    setIsFullScreen,
    currentStepFormData.attributeDefaultValue,
    addAccessAttributeMappingLogic,
    fullScreenEntitlementIndex,
    handleFormSubmit,
    removeAccessLogic,
    dispatch,
  ]);

  const renderContent = () => {
    // Special handling for RemoveAccess AttributeMapping with entitlementIndex
    if (fullScreenStep.includes('Remove Access')) {
      return (
        <RemoveAccessAttributeMappingExpandedView
          entitlementIndex={Math.max(0, fullScreenEntitlementIndex)}
        />
      );
    }

    // Handle AddAccess AttributeMapping - use entitlementIndex or default to 0
    if (fullScreenStep.includes('Mapping')) {
      return (
        <AttributeMappingExpandedView
          entitlementIndex={Math.max(0, fullScreenEntitlementIndex)}
        />
      );
    }

    // Special handling for AddAccess without entitlementIndex - use dedicated component
    if (actualStepName === 'AddAccess') {
      return <AddAccessExpandedView />;
    }

    return (
      <StepRenderer
        stepName={actualStepName}
        validationFields={localFields}
        className='Integrations-ExpandedForm-Container'
      />
    );
  };

  if (!isFullscreen) {
    return null;
  }

  // Use standard layout for all steps including AddAccess
  return (
    <div className='Integrations-ExpandedForm'>
      <div className='Integrations-ExpandedForm-header'>
        <div className='Integrations-ExpandedForm-header-Left'>
          <Typography kind='h2' desktop='h2-d' mobile='h2-d'>
            {fullScreenStep}
          </Typography>
        </div>
        <div className='Integrations-ExpandedForm-header-Right'>
          <ButtonIcon
            kind='ghost'
            icon={<Icon kind='ExitFullScreen' size='medium' />}
            onClick={() => {
              setIsFullScreen(false);

              const hasEntitlementIndex = fullScreenEntitlementIndex >= 0;
              const isAccessStep =
                actualStepName === 'AddAccess' ||
                actualStepName === 'RemoveAccess';

              if (!hasEntitlementIndex || !isAccessStep) {
                setTimeout(() => {
                  dispatch(addVisitedStep(actualStepName));
                  dispatch(setActiveStep(actualStepName));
                }, 10);
              }
            }}
          />
        </div>
      </div>

      <div className='Integrations-ExpandedForm-Body'>
        {validationError !== null && (
          <ErrorBanner message={validationError} expanded />
        )}
        {renderContent()}
      </div>

      <div className='Integrations-ExpandedForm-Footer'>
        <Button
          kind={fullScreenButtonKind}
          onClick={handleSave}
          type='button'
          isDisabled={actualStepName === 'AddAccess' && addAccessInputField}>
          {fullScreenButtonLabel}
        </Button>
      </div>
    </div>
  );
}

export default ExpandedFormView;
